import { Component, Input, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyFormFieldAppearance as MatFormFieldAppearance } from '@angular/material/legacy-form-field';
import { Subscription } from 'rxjs';

@Component({
  selector: 'form-select-between',
  templateUrl: './form-select-between.component.html',
  styleUrls: ['./form-select-between.component.scss'],
})
export class FormSelectBetweenComponent implements OnInit, OnDestroy {
  @Input() formGroup: UntypedFormGroup;
  @Input() controlName!: string;
  @Input() label = '';
  @Input() appearance: MatFormFieldAppearance = 'outline';
  @Input() hint = '';
  @Input() icon = '';
  @Input() withoutPadding = false;
  @Input() defaultErrorMessage: any;
  @Input() invalidPatternMessage = 'Le modèle est invalide';
  @Input() customErrorMessage = '';
  @Input() minMaxField = ['min', 'max'];
  @Input() typeSelection: 'number' | 'date';
  @Input() selection: 'between' | 'fixed' | 'greaterThan' | 'lessThan' = 'between';
  @Output() onLostFocus = new EventEmitter<void>();

  private comparisonSubscription?: Subscription;
  private minSubscription?: Subscription;
  private maxSubscription?: Subscription;

  ngOnInit(): void {
    this.adjustFieldState();
    this.formGroup.get(this.controlName)?.setValue(this.selection);

    this.comparisonSubscription = this.formGroup.get(this.controlName)?.valueChanges.subscribe(value => {
      if (!value) {
        this.selection = 'between';
        this.formGroup.get(this.controlName)?.setValue('between', { emitEvent: false });
      } else if (value !== this.selection) {
        this.selection = value;
      }
      this.adjustFieldState();
    });

    const minControl = this.formGroup.get(this.minMaxField[0]);
    const maxControl = this.formGroup.get(this.minMaxField[1]);

    this.minSubscription = minControl?.valueChanges.subscribe(() => this.syncBetweenRequired());
    this.maxSubscription = maxControl?.valueChanges.subscribe(() => this.syncBetweenRequired());
  }

  ngOnDestroy(): void {
    this.comparisonSubscription?.unsubscribe();
    this.minSubscription?.unsubscribe();
    this.maxSubscription?.unsubscribe();
  }

  onSelectionChange(event: any): void {
    this.selection = event.value;
    this.adjustFieldState();
  }

  adjustFieldState(): void {
    const minControl = this.formGroup.get(this.minMaxField[0]);
    const maxControl = this.formGroup.get(this.minMaxField[1]);

    if (this.selection === 'between') {
      minControl?.enable();
      maxControl?.enable();
      this.syncBetweenRequired();
    } else {
      minControl?.reset();
      minControl?.disable({ emitEvent: false });
      minControl?.clearValidators();
      minControl?.updateValueAndValidity({ emitEvent: false });
      maxControl?.enable();
      maxControl?.clearValidators();
      maxControl?.updateValueAndValidity({ emitEvent: false });
    }
  }

  private isEmpty(value: any): boolean {
    return value === null || value === undefined || value === '';
  }

  private syncBetweenRequired(): void {
    if (this.selection !== 'between') return;

    const minControl = this.formGroup.get(this.minMaxField[0]);
    const maxControl = this.formGroup.get(this.minMaxField[1]);
    if (!minControl || !maxControl) return;

    const minEmpty = this.isEmpty(minControl.value);
    const maxEmpty = this.isEmpty(maxControl.value);

    const minShouldBeRequired = !maxEmpty && minEmpty;
    const maxShouldBeRequired = !minEmpty && maxEmpty;

    minControl.setValidators(minShouldBeRequired ? [Validators.required] : []);
    minControl.updateValueAndValidity({ emitEvent: false });
    if (minShouldBeRequired) minControl.markAsTouched();

    maxControl.setValidators(maxShouldBeRequired ? [Validators.required] : []);
    maxControl.updateValueAndValidity({ emitEvent: false });
    if (maxShouldBeRequired) maxControl.markAsTouched();
  }

  getUniqueControlName(): string {
    // Utilise le label comme préfixe si disponible, sinon utilise le controlName
    const prefix = this.label ? this.label.replace(/\s+/g, '_').toLowerCase().replace(/[^\w]/g, '') : 'field';
    return `${prefix}_${this.controlName}`;
  }
}
